import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Keyboard, Layers, LoaderCircle, MapPin, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'utils/classnames';
import {
  DISPOSITION_COLORS,
  PALLET_BUILD_OPERATION_TYPES,
  PALLET_LICENSE_STATUS_TYPES,
  SCREEN_TYPES,
} from 'utils/constants';
import { useAppStore } from 'store';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { usePalletBuildStore } from 'store/palletBuildStore';
import { useAuthStore } from 'store/authStore';
import { buildPallet } from 'services/api/palletBuild';
import { playCallbackSound, playErrorSound, playGoodSound } from 'utils/sounds';
import { isValidLicenseId } from 'utils/validation';

type Step = 'pallet' | 'weight' | 'location';

export const WeighClosePage = () => {
  const { pallet_id, pallet, loadPallet, clearPallet, reset } = usePalletBuildStore();
  const [palletScan, setPalletScan] = useState(pallet_id ?? '');
  const [weight, setWeight] = useState('');
  const [locationScan, setLocationScan] = useState('');
  const [locationId, setLocationId] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [closing, setClosing] = useState(false);
  const palletRef = useRef<HTMLInputElement>(null);
  const weightRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible: palletKeyboard, toggleKeyboardInput: togglePalletKeyboard } =
    useKeyboardInputToggle(palletRef);
  const { keyboardVisible: weightKeyboard, toggleKeyboardInput: toggleWeightKeyboard } =
    useKeyboardInputToggle(weightRef);
  const { keyboardVisible: locationKeyboard, toggleKeyboardInput: toggleLocationKeyboard } =
    useKeyboardInputToggle(locationRef);
  const { setCurrentScreen } = useAppStore();

  const [step, setStep] = useState<Step>('pallet');
  const autoLoadedRef = useRef(false);

  const activePalletId = pallet_id ?? palletScan.trim();
  const isPalletClosed = pallet?.license_status === PALLET_LICENSE_STATUS_TYPES.CLOSED;

  // Focusing here (rather than right after setStep) matters: setStep is async, so calling
  // .focus() in the same handler still sees the input as disabled from the prior render.
  useEffect(() => {
    if (step === 'weight') weightRef.current?.focus();
    if (step === 'location') locationRef.current?.focus();
  }, [step]);

  const scanPallet = async (id: string) => {
    try {
      const result = await loadPallet(id, PALLET_BUILD_OPERATION_TYPES.INSPECT);
      if (result.responseState !== 'confirmed') {
        setErrorMessage(result.responseMsg || 'Failed to scan pallet. Please try again.');
        setHasError(true);
        playErrorSound();
        return;
      }
      if (result.weight_kg) setWeight(String(result.weight_kg));
      if (result.pallet_location_id && result.pallet_location_id !== 'unassigned') {
        setLocationId(result.pallet_location_id);
      }
      result.license_status === PALLET_LICENSE_STATUS_TYPES.CLOSED
        ? playCallbackSound()
        : playGoodSound();
      setStep('weight');
    } catch {
      setErrorMessage('Failed to scan pallet. Please try again.');
      setHasError(true);
      playErrorSound();
    }
  };

  useEffect(() => {
    if (autoLoadedRef.current || !pallet_id || !pallet) return;
    autoLoadedRef.current = true;
    if (pallet.weight_kg) setWeight(String(pallet.weight_kg));
    if (pallet.pallet_location_id && pallet.pallet_location_id !== 'unassigned') {
      setLocationId(pallet.pallet_location_id);
    }
    setStep('weight');
  }, [pallet_id, pallet]);

  const handlePalletKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (palletScan.trim()) {
        setHasError(false);
        if (!isValidLicenseId(palletScan.trim())) {
          setErrorMessage('Pallet ID must be 20 digits.');
          setHasError(true);
          playErrorSound();
          return;
        }
        await scanPallet(palletScan.trim());
      }
    }
  };

  const handleClearPallet = () => {
    clearPallet();
    setPalletScan('');
    setWeight('');
    setLocationScan('');
    setLocationId(null);
    setHasError(false);
    autoLoadedRef.current = false;
    setStep('pallet');
    requestAnimationFrame(() => palletRef.current?.focus());
  };

  const handleWeightKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isPalletClosed) return;
      const parsed = parseFloat(weight);
      if (!weight.trim() || Number.isNaN(parsed) || parsed <= 0) {
        toast.error('Enter a valid weight.');
        playErrorSound();
        return;
      }
      setStep('location');
    }
  };

  const handleLocationKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = locationScan.trim();
      if (!trimmed) return;
      setHasError(false);
      if (!activePalletId) {
        setLocationScan('');
        setErrorMessage('Scan a pallet first.');
        setHasError(true);
        requestAnimationFrame(() => locationRef.current?.focus());
        return;
      }
      try {
        const user_id = useAuthStore.getState().user?.id ?? '';
        const result = await buildPallet({
          operation_id: PALLET_BUILD_OPERATION_TYPES.LOCATE,
          user_id,
          license_id: activePalletId,
          stage_id: trimmed,
          weight_kg: parseFloat(weight),
        });
        if (result?.responseState === 'confirmed') {
          setLocationId(trimmed);
          playGoodSound();
        } else {
          setLocationScan('');
          setErrorMessage(result?.responseMsg || "Location doesn't exist. Please scan again.");
          setHasError(true);
          playErrorSound();
          requestAnimationFrame(() => locationRef.current?.focus());
        }
      } catch {
        setLocationScan('');
        setErrorMessage("Location doesn't exist. Please scan again.");
        setHasError(true);
        playErrorSound();
        requestAnimationFrame(() => locationRef.current?.focus());
      }
    }
  };

  const handleClose = async () => {
    if (!activePalletId || !weight.trim() || !locationId) return;
    setHasError(false);
    setClosing(true);
    try {
      const user_id = useAuthStore.getState().user?.id ?? '';
      const result = await buildPallet({
        operation_id: PALLET_BUILD_OPERATION_TYPES.STAGE,
        user_id,
        license_id: activePalletId,
        stage_id: locationId,
        weight_kg: parseFloat(weight),
      });
      if (!result || result.responseState !== 'confirmed') {
        setErrorMessage(result?.responseMsg || 'Failed to close pallet. Please try again.');
        setHasError(true);
        playErrorSound();
        return;
      }
      reset();
      playGoodSound();
      setCurrentScreen(SCREEN_TYPES.PALLET_BUILD);
    } catch {
      setErrorMessage('Failed to close pallet. Please try again.');
      setHasError(true);
      playErrorSound();
    } finally {
      setClosing(false);
    }
  };

  const onCancel = () => {
    clearPallet();
    setCurrentScreen(SCREEN_TYPES.PALLET_BUILD);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 py-4',
        hasError
          ? DISPOSITION_COLORS.PROBLEM.gradient
          : isPalletClosed
            ? DISPOSITION_COLORS.EXCEPTION.gradient
            : '',
        (hasError || isPalletClosed) && `-mx-4 -mb-6 min-h-full px-6 pb-6 pt-4 `
      )}
    >
      <div className="flex flex-col items-center gap-1">
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-widest',
            hasError || isPalletClosed ? 'text-white/80' : 'text-emerald-500'
          )}
        >
          Pallet Build · Step 3
        </span>
        <h2
          className={cn(
            'text-2xl font-bold',
            hasError || isPalletClosed ? 'text-white' : 'text-slate-800'
          )}
        >
          Weigh & Close
        </h2>
        {hasError ? (
          <p className="text-3xl font-bold text-white text-center">{errorMessage}</p>
        ) : (
          isPalletClosed && (
            <p className="text-3xl font-bold text-white text-center">
              Pallet is closed — weight cannot be changed
            </p>
          )
        )}
      </div>

      <div className="flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
          <Scale className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
            Close pallet to staged
          </span>
          <span className="text-sm text-slate-400">Record weight and close the pallet</span>
        </div>
      </div>

      {step === 'pallet' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <Label className="mb-2 block text-sm font-semibold text-slate-600">Pallet Barcode</Label>
          <div className="flex items-center gap-2">
            <Input
              ref={palletRef}
              type="text"
              inputMode={palletKeyboard ? 'text' : 'none'}
              placeholder="Scan pallet ID..."
              value={palletScan}
              onChange={(e) => setPalletScan(e.target.value)}
              onKeyDown={handlePalletKeyDown}
              onBlur={() => requestAnimationFrame(() => palletRef.current?.focus())}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              className="text-base"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={togglePalletKeyboard}
              aria-pressed={palletKeyboard}
              className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
            >
              <Keyboard className="h-5 w-5" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
          <Layers className="h-4 w-4 text-emerald-500 shrink-0" />
          <div className="flex flex-1 flex-col min-w-0">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Pallet
            </span>
            <span className="font-mono text-sm font-bold text-slate-800 truncate">
              {activePalletId}
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleClearPallet}
            className="h-8 shrink-0 rounded-lg px-3 text-xs font-bold"
          >
            Clear
          </Button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">
          Weight <span className="font-normal text-slate-400">(kg)</span>
        </Label>
        <div className="flex items-center gap-2">
          <Input
            ref={weightRef}
            type="number"
            inputMode={weightKeyboard ? 'decimal' : 'none'}
            placeholder="Enter weight..."
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            onKeyDown={handleWeightKeyDown}
            onBlur={() =>
              step === 'weight' && requestAnimationFrame(() => weightRef.current?.focus())
            }
            disabled={step === 'pallet' || isPalletClosed}
            autoComplete="off"
            className="text-base"
            min="0"
            step="0.1"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleWeightKeyboard}
            aria-pressed={weightKeyboard}
            disabled={step === 'pallet' || isPalletClosed}
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">Location</Label>
        {locationId ? (
          <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
            <MapPin className="h-4 w-4 text-emerald-500 shrink-0" />
            <div className="flex flex-1 flex-col min-w-0">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Location
              </span>
              <span className="font-mono text-sm font-bold text-slate-800 truncate">
                {locationId}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setLocationId(null);
                setLocationScan('');
                setStep('location');
                requestAnimationFrame(() => locationRef.current?.focus());
              }}
              className="h-8 shrink-0 rounded-lg px-3 text-xs font-bold"
            >
              Change
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Input
              ref={locationRef}
              type="text"
              inputMode={locationKeyboard ? 'text' : 'none'}
              placeholder="Scan location..."
              value={locationScan}
              onChange={(e) => setLocationScan(e.target.value)}
              onKeyDown={handleLocationKeyDown}
              onBlur={() =>
                step === 'location' && requestAnimationFrame(() => locationRef.current?.focus())
              }
              disabled={!activePalletId || step !== 'location'}
              autoComplete="off"
              spellCheck={false}
              className="text-base"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={toggleLocationKeyboard}
              aria-pressed={locationKeyboard}
              disabled={!activePalletId || step !== 'location'}
              className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
            >
              <Keyboard className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>

      <div className="buttons-container !bg-transparent">
        {!isPalletClosed && (
          <Button
            onClick={handleClose}
            disabled={!activePalletId || !weight.trim() || !locationId || isPalletClosed || closing}
            className="flex-1 h-12 bg-emerald-600 text-base hover:bg-emerald-700"
          >
            {closing ? (
              <>
                <LoaderCircle className="h-5 w-5 animate-spin" />
                Closing...
              </>
            ) : (
              'Close Pallet'
            )}
          </Button>
        )}

        <Button onClick={onCancel} variant="secondary" className="flex-1 h-12" disabled={closing}>
          Back
        </Button>
      </div>
    </div>
  );
};
